import type { CSSProperties } from "react";
import { parseApiInstant, todayInZone } from "../../lib/localeFormat";

export type DueUrgency = "green" | "amber" | "red";
export type DueFilter = "all" | DueUrgency;

export type CycleTaskLike = {
  status: "todo" | "doing" | "done";
  due_at?: string | null;
};

export type CycleStageLike = {
  id: string;
  name: string;
  sla_hours?: number | null;
  flow_status?: "not_started" | "started" | "done" | null;
  starts_at?: string | null;
  ends_at?: string | null;
  tasks: CycleTaskLike[];
};

export type AnnotatedCycleStage<T extends CycleStageLike = CycleStageLike> = T & {
  index: number;
  active: boolean;
  complete: boolean;
  urgency: DueUrgency;
};

const MS_PER_DAY = 86_400_000;
const MS_PER_HOUR = 3_600_000;
const URGENCY_RANK: Record<DueUrgency, number> = { green: 0, amber: 1, red: 2 };

const DUE_LABEL: Record<DueUrgency, string> = {
  green: "On track",
  amber: "Due soon",
  red: "Overdue",
};

/** Green until 3+ days out, amber 1–2 days before due, red on the due day and after. */
export function dueUrgency(dueAt: string, now = new Date(), timezone = "UTC"): DueUrgency {
  const due = parseApiInstant(dueAt);
  if (Number.isNaN(due.getTime())) return "green";
  // Days are counted on the viewer's calendar, not UTC's.
  const dueDay = Date.parse(todayInZone(timezone, due));
  const nowDay = Date.parse(todayInZone(timezone, now));
  const diffDays = Math.round((dueDay - nowDay) / MS_PER_DAY);
  if (diffDays <= 0) return "red";
  if (diffDays <= 2) return "amber";
  return "green";
}

export function dueUrgencyLabel(urgency: DueUrgency): string {
  return DUE_LABEL[urgency];
}

export function dueCardBackground(urgency: DueUrgency): string {
  return `var(--color-due-${urgency}-bg)`;
}

/** Card paints `background` inline, so the tint has to ride on the same style object. */
export function dueCardStyle(urgency: DueUrgency): CSSProperties {
  return {
    background: dueCardBackground(urgency),
    borderLeft: `var(--space-1) solid var(--color-due-${urgency}-edge)`,
  };
}

export function matchesDueFilter(
  dueAt: string | null | undefined,
  filter: DueFilter,
  now = new Date(),
  timezone = "UTC",
): boolean {
  if (filter === "all") return true;
  if (!dueAt) return false;
  return dueUrgency(dueAt, now, timezone) === filter;
}

export function stageIsComplete(tasks: CycleTaskLike[]): boolean {
  return tasks.length > 0 && tasks.every((task) => task.status === "done");
}

/** Worst remaining due colour on the stage. Completed stages stay green. */
export function stageUrgency(tasks: CycleTaskLike[], now = new Date(), timezone = "UTC"): DueUrgency {
  const open = tasks.filter((task) => task.status !== "done");
  let worst: DueUrgency = "green";
  for (const task of open) {
    if (!task.due_at) continue;
    const next = dueUrgency(task.due_at, now, timezone);
    if (URGENCY_RANK[next] > URGENCY_RANK[worst]) worst = next;
  }
  return worst;
}

/**
 * A stage lights up once the customer has reached it: first stage, previous
 * stage finished, work already started, a task is due, or the SLA window opened.
 */
export function stageIsActive(
  stage: CycleTaskLike[] | { tasks: CycleTaskLike[] },
  previous: CycleTaskLike[] | { tasks: CycleTaskLike[] } | null,
  startedAt: string | null | undefined,
  hoursBeforeStage: number,
  now = new Date(),
): boolean {
  const tasks = Array.isArray(stage) ? stage : stage.tasks;
  const previousTasks = previous == null ? null : Array.isArray(previous) ? previous : previous.tasks;
  if (!previousTasks) return true;
  if (stageIsComplete(previousTasks)) return true;
  if (tasks.some((task) => task.status !== "todo")) return true;
  const dueTimes = tasks
    .map((task) => task.due_at)
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value).getTime())
    .filter((time) => !Number.isNaN(time));
  if (dueTimes.some((time) => time <= now.getTime())) return true;
  if (startedAt) {
    const start = new Date(startedAt).getTime();
    if (!Number.isNaN(start) && start + hoursBeforeStage * MS_PER_HOUR <= now.getTime()) {
      return true;
    }
  }
  return false;
}

export function annotateCycleStages<T extends CycleStageLike>(
  stages: T[],
  startedAt: string | null | undefined,
  now = new Date(),
  timezone = "UTC",
): AnnotatedCycleStage<T>[] {
  let hoursBeforeStage = 0;
  return stages.map((stage, index) => {
    const previous = index === 0 ? null : stages[index - 1];
    const annotated: AnnotatedCycleStage<T> = {
      ...stage,
      index,
      active: stageIsActive(stage, previous, startedAt, hoursBeforeStage, now),
      complete: stageIsComplete(stage.tasks),
      urgency: stageUrgency(stage.tasks, now, timezone),
    };
    if (stage.flow_status === "started" || stage.flow_status === "done") {
      annotated.active = true;
    } else if (stage.flow_status === "not_started") {
      annotated.active = stage.tasks.some((task) => task.status !== "todo") || annotated.complete;
    }
    hoursBeforeStage += Number(stage.sla_hours || 0);
    return annotated;
  });
}

export function currentStageId(stages: Array<{ id: string; active: boolean; complete: boolean }>): string | null {
  const live = stages.find((stage) => stage.active && !stage.complete);
  if (live) return live.id;
  const lastActive = [...stages].reverse().find((stage) => stage.active);
  return lastActive?.id ?? stages[0]?.id ?? null;
}
